/**
 * Telnyx WebRTC Configuration Constants
 * Extracted from TelnyxWebRTCService for maintainability
 */

export const TELNYX_CONSTANTS = {
  /** Reconnection settings */
  RECONNECT: {
    MAX_ATTEMPTS: 5,
    BASE_DELAY_MS: 1000,
    MAX_DELAY_MS: 30000,
  },

  /** Audio handling settings */
  AUDIO: {
    POLL_INTERVAL_MS: 300,
    POLL_TIMEOUT_MS: 30000,
    RINGBACK_DELAY_MS: 1500,
    PLAYBACK_VERIFY_DELAY_MS: 1000,
    POST_RENEGOTIATION_WAIT_MS: 1500,
  },

  /** Various timeout values */
  TIMEOUTS: {
    ANSWER_FLAG_CLEAR_MS: 2000,
    AUDIO_DEVICE_SETUP_MS: 1000,
    STREAM_WAIT_MS: 1000,
    MAX_STREAM_ATTEMPTS: 20,
    RENEGOTIATION_ATTEMPTS: 10,
    ICE_TEST_TIMEOUT_MS: 10000,
    PLAYBACK_VERIFY_MS: 2000,
    RING_TIMEOUT_MS: 30000, // Hang up outbound call if not answered within 30 seconds
  },

  /** STUN/TURN servers for ICE negotiation */
  ICE_SERVERS: [
    { urls: 'stun:stun.telnyx.com:3478' },
    { urls: 'stun:stun.l.google.com:19302' },
  ] as RTCIceServer[],

  /** SIP configuration defaults */
  SIP: {
    DEFAULT_DOMAIN: 'sip.telnyx.com',
    DEFAULT_REALM: 'sip.telnyx.com',
  },

  /** Error codes used throughout the service */
  ERROR_CODES: {
    INIT_FAILED: 'INIT_FAILED',
    AUTH_ERROR: 'AUTH_ERROR',
    NETWORK_ERROR: 'NETWORK_ERROR',
    CONNECTION_ERROR: 'CONNECTION_ERROR',
    SIP_REGISTRATION_ERROR: 'SIP_REGISTRATION_ERROR',
    CALL_FAILED: 'CALL_FAILED',
    ANSWER_FAILED: 'ANSWER_FAILED',
    HANGUP_FAILED: 'HANGUP_FAILED',
    HOLD_FAILED: 'HOLD_FAILED',
    MUTE_FAILED: 'MUTE_FAILED',
    DTMF_FAILED: 'DTMF_FAILED',
    DEVICE_ENUMERATION_FAILED: 'DEVICE_ENUMERATION_FAILED',
    SET_DEVICE_FAILED: 'SET_DEVICE_FAILED',
    MIC_PERMISSION_DENIED: 'MIC_PERMISSION_DENIED',
    AUDIO_DEVICE_ERROR: 'AUDIO_DEVICE_ERROR',
    MAX_RECONNECT_ATTEMPTS: 'MAX_RECONNECT_ATTEMPTS',
    NO_ACTIVE_CALL: 'NO_ACTIVE_CALL',
    INVALID_CALL_STATE: 'INVALID_CALL_STATE',
  },

  /** Event names emitted by the service */
  EVENTS: {
    // Connection events
    READY: 'ready',
    SOCKET_CLOSE: 'socket-close',
    SOCKET_ERROR: 'socket-error',
    RECONNECTING: 'reconnecting',
    RECONNECTED: 'reconnected',

    // Call events
    INCOMING_CALL: 'incoming-call',
    CALL_INITIATED: 'call-initiated',
    CALL_ANSWERED: 'call-answered',
    CALL_ENDED: 'call-ended',
    CALL_TIMEOUT: 'call-timeout',
    DTMF_SENT: 'dtmf-sent',
    NOTIFICATION: 'notification',

    // Audio events
    AUTOPLAY_BLOCKED: 'autoplay_blocked',
    AUDIO_ROUTING_FAILED: 'audio_routing_failed',
  },
} as const;

/** Type for error codes */
export type TelnyxErrorCode = keyof typeof TELNYX_CONSTANTS.ERROR_CODES;

/** Type for event names */
export type TelnyxEventName =
  | typeof TELNYX_CONSTANTS.EVENTS[keyof typeof TELNYX_CONSTANTS.EVENTS];
